import { ApckNarrativeNode, DownloadImagesMixin, ModalScreencastGallery } from '../goscript/narrative';

const TYPES_MAP = {
    log_info: {
        type: 'secondary',
        color: 'light-slate',
        icon: 'fa-circle-arrow-right',
    },
    log_notice: {
        type: 'info',
        color: 'blue',
        icon: 'fa-info-circle',
    },
    log_error: {
        type: 'destructive',
        color: 'red',
        icon: 'fa-exclamation-circle',
    },
    log_success: {
        type: 'success',
        color: 'green',
        icon: 'fa-check-circle',
    },
    log_warning: {
        type: 'warning',
        color: 'yellow',
        icon: 'fa-exclamation-triangle',
    },
    thinking: {
        type: 'info',
        color: 'blue',
        icon: 'fa-brain'
    },
    script: {
        type: 'secondary',
        color: 'light-slate',
        icon: 'fa-code'
    },
}



export const ApckLogConsole = {
    // TODO: Move the scrollable container into this component
    mixins: [DownloadImagesMixin],
    components: {
        'apck-narrative-node': ApckNarrativeNode,
    },
    props: {
        events: {
            type: Array,
            default: [],
        },
        attachmentUrl: {
            type: String,
            default: '/vuln-control/attachment',
        },
        running: {
            type: Boolean,
            default: false,
        },
        runningName: {
            type: String,
            default: "Running...",
        },
        showTimestamp: {
            type: Boolean,
            default: true,
        },
        autoscroll: {
            type: Boolean,
            default: true,
        },
    },
    provide() {
        return {
            attachmentUrl: this.attachmentUrl,
            containerAsPanel: true,
            screencastEvents: () => this.events,
        };
    },
    data() {
        return {
            collapsed: {},
        };
    },
    computed: {
        indentedItems() {
            let chapterId = null;
            let sceneId = null;

            this.visibleItems.forEach(item => {
                if (item.type === 'narrative_item' && item.narrativeNode) {
                    const type = item.narrativeNode.type;
                    if (type === 'chapter') {
                        chapterId = item.id;
                        sceneId = null;
                        this.$set(item, 'indent', 0);
                        this.$set(item, 'visible', true);
                        return;
                    }
                    if (type === 'scene') {
                        sceneId = item.id;
                        this.$set(item, 'indent', 1);
                        this.$set(item, 'visible', !this.collapsed[chapterId]);
                        return;
                    }
                    if (type === 'close_scene') { sceneId = null; }
                    if (type === 'close_chapter') { sceneId = null; chapterId = null; }
                }
                const indent = (chapterId !== null ? 1 : 0) + (sceneId !== null ? 1 : 0);
                this.$set(item, 'indent', indent);
                this.$set(item, 'visible', !this.collapsed[chapterId] && !this.collapsed[sceneId]);
            });

            for (let i = this.visibleItems.length - 1; i >= 0; i--) {
                if (this.visibleItems[i].type === 'narrative_item') break;
                this.$set(this.visibleItems[i], 'indent', 0);
            }

            return this.visibleItems;
        },
        visibleItems() {
            const allowedEvents = [
                'log_info',
                'log_notice',
                'log_error',
                'log_success',
                'log_warning',
                'thinking',
                'script',
                'narrative_item',
            ];
            return this.events.filter(item => allowedEvents.includes(item.type));
        },
        eventCount() {
            if (Array.isArray(this.events)) return this.events.length;
            return 0;
        },
        hasCollapsibleItems() {
            return this.visibleItems.some(item => {
                const type = item.narrativeNode?.type;
                return type === 'chapter' || type === 'scene';
            });
        },
        hasScreencastFrames() {
            return this.visibleItems.some(item =>
                item.type === 'narrative_item' && item.narrativeNode?.serialised === 'ScreencastFrame'
            );
        },
    },
    methods: {
        logData(type) {
            return TYPES_MAP[type] || TYPES_MAP.log_info;
        },
        makeLabel(item) {
            let label = item.data || '';
            if (item.type === 'script') label = 'GoScript updated';
            if (item.type === 'thinking' && !item.data) label = 'Processing...';
            return label;
        },
        lineLabel(item) {
            return 'Ln ' + item.lineno + ', Col ' + item.column;
        },
        goToLine(item) {
            this.$emit("log-error", item);
        },
        openScreenshot(item) {
            this.$emit("log-screenshot", item.id);
        },
        openScript(item) {
            this.$emit("log-script", item);
        },
        toggleCollapse(id) {
            this.$set(this.collapsed, id, !this.collapsed[id]);
        },
        collapseAll() {
            this.visibleItems.forEach(item => {
                const type = item.narrativeNode?.type;
                if (type === 'chapter' || type === 'scene') {
                    this.$set(this.collapsed, item.id, true);
                }
            });
        },
        expandAll() {
            this.collapsed = {};
        },
        openScreencastGallery() {
            this.$modal.open({
                title: 'Screencast',
                component: ModalScreencastGallery,
                bind: {
                    events: this.events,
                    attachmentUrl: this.attachmentUrl,
                },
                fullHeight: true,
                size: 'lg',
            });
        },
        processNarrativeItems(events) {
            // We initiate the process of fetching image URLs for narrative items so they can be output in the list.
            // TODO: Consider whether we can move this process out of the log console.
            // Right now, we try to load them one by one, not all at once.
            events.forEach(event => {
                if (event.type !== 'narrative_item' || event.narrativeNode) return;
                try {
                    const node = JSON.parse(event.data);
                    this.$set(event, 'narrativeNode', node);
                    this.downloadImages(node);
                } catch (e) {}
            });
        },
        getScrollElement(el) {
            // Find the nearest scrollable parent.
            // TODO: Move the scrollable container into this component
            if (!el) return null;

            let node = el.parentElement;
            while (node && node !== document.body) {
                const { overflowY } = window.getComputedStyle(node);

                if (overflowY === "auto" || overflowY === "scroll") {
                    return node;
                }

                node = node.parentElement;
            }

            return null;
        },

        scrollToBottom() {
            if (!this._scrollElement) return;
            this._scrollElement.scrollTop = this._scrollElement.scrollHeight;
        },

        runAutoscroll() {
            if (!this.autoscroll) return;
            if (!this._scrollElement) return
            let distance =  this._scrollElement.scrollHeight
                    - this._scrollElement.scrollTop
                    - this._scrollElement.clientHeight;

            this.$nextTick(() => {
                if (distance <= 80) {
                    // Autoscroll only if the user is near the bottom.
                    // Do not autoscroll when the user has scrolled up.
                    this.scrollToBottom();
                }
            });
        },
    },
    mounted() {
        this._scrollElement = this.getScrollElement(this.$el);
        this.runAutoscroll();
    },
    watch: {
        events: {
            handler(events, prev) {
                if (prev && events !== prev) this.collapsed = {};
                this.processNarrativeItems(events);
            },
            immediate: true,
        },
        eventCount() {
            this.runAutoscroll();
        },
    },
    template: `<div class="new-ui">
        <ui-panel-base v-if="hasCollapsibleItems || hasScreencastFrames" align="center" justify="end" gap="2" class="no-shrink">
            <ui-button type="link" label="Expand all" @click="expandAll" />
            <ui-button type="link" label="Collapse all" @click="collapseAll" />
        </ui-panel-base>
        <ui-layout
            class="grow w-full"
            direction="column"
            tag="ul"
            gap="2"
        >
            <template v-for="item in indentedItems">
                <li
                    v-if="item.type === 'narrative_item' && item.narrativeNode"
                    :key="item.id"
                    v-show="item.visible"
                    :class="['apck-narrative__item--depth-' + item.indent, { 'apck-narrative__item--collapsible': item.narrativeNode.type === 'chapter' || item.narrativeNode.type === 'scene' }]"
                    @click="item.narrativeNode.type === 'chapter' || item.narrativeNode.type === 'scene' ? toggleCollapse(item.id) : null"
                >
                    <apck-narrative-node
                        :node="item.narrativeNode"
                        :collapsed="!!collapsed[item.id]"
                    />
                </li>
                <ui-panel-base
                    v-else
                    :key="item.id"
                    tag="li"
                    v-show="item.visible"
                    :color="logData(item.type).color"
                    align="center"
                    justify="space-between"
                    :class="['no-shrink', 'grow', 'apck-narrative__item--depth-' + item.indent]"
                >
                    <ui-layout align="center" justify="start">
                        <ui-icon :icon="logData(item.type).icon" :type="logData(item.type).type" />
                        <span class="log-console-item__data"><i v-if="showTimestamp && item.timestamp">{{item.timestamp}} - </i>{{ makeLabel(item) }}</span>
                    </ui-layout>
                    <ui-layout align="center">
                        <ui-button v-if="item.data && item.data.includes('screenshot')" type="link" label="Open" @click="openScreenshot(item)" />
                        <ui-button v-if="item.lineno" type="link" :label="lineLabel(item)" @click="goToLine(item)" />
                        <ui-button v-if="item.type === 'script'" type="link" label="Open" @click="openScript(item)" />
                    </ui-layout>
                </ui-panel-base>
            </template>
            <ui-panel-base v-if="running" tag="li" color="light-slate" align="center" justify="start" gap="2" class="no-shrink grow">
                <i aria-hidden="true" class="fa-regular fa-spinner fa-spin icon icon--md icon--secondary"></i>
                <span>{{ runningName }}</span>
            </ui-panel-base>
        </ui-layout>
    </div>
    `,
};